/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { ISearchResultModalViewProps } from '@msdyn365-commerce-modules/search-result-container/src/modules/search-result-container/./components';
import { ICategoryHierarchyViewProps, IRefineMenuViewProps, ISearchResultContainerViewProps, ISortByViewProps, ITitleViewProps } from '@msdyn365-commerce-modules/search-result-container/src/modules/search-result-container/./search-result-container';

const SearchResultContainerView: React.FC<ISearchResultContainerViewProps> = props => {
	const { SearchResultContainer, products, pagination, ProductSectionContainer, choiceSummary, isMobile, modalToggle, searchResultModal, TitleViewProps, refineMenu, categoryHierarchy, sortByOptions, CategoryNavContainer, RefineAndProductSectionContainer, errorMessage, FeatureSearchContainer, similarLookProduct } = props;
	const isRecoSearchPage = props.context.actionContext.requestContext.query?.recommendation;
	if(isMobile){
		return (
			<Module {...SearchResultContainer}>
				{renderCategoryHierarchy(categoryHierarchy)}
				{renderTitle(TitleViewProps)}
				{choiceSummary}
				{modalToggle}
				{createSearchResultModal(searchResultModal, refineMenu, sortByOptions, isRecoSearchPage)}
				<Node {...FeatureSearchContainer}>
					{similarLookProduct}
				</Node>
				<div className="ms-products-container">
					{errorMessage}
					{products}
				</div>
				{pagination}
			</Module>
		);
	}
	return (
		<Module {...SearchResultContainer}>
			{TitleViewProps && renderTitle(TitleViewProps)}
			<Node {...CategoryNavContainer}>
				{categoryHierarchy && renderCategoryHierarchy(categoryHierarchy)}
				{TitleViewProps && renderTitleCount(TitleViewProps)}
			</Node>
			<div className="row">
				<div className="col-filters">
					{sortByOptions && !isRecoSearchPage && renderSort(sortByOptions)}
					{refineMenu && renderRefiner(refineMenu)}
				</div>
				<Node {...RefineAndProductSectionContainer}>
					{choiceSummary}
					<div className="ms-products-container">
						{errorMessage}
						{products}
					</div>
					{pagination}
				</Node>
			</div>
			<Node {...ProductSectionContainer}>
				<Node {...FeatureSearchContainer}>
					{similarLookProduct}
				</Node>
			</Node>
		</Module>
	);
};

const createSearchResultModal = (modalProps: ISearchResultModalViewProps, refineMenu: IRefineMenuViewProps, sortByDropDown: ISortByViewProps, isRecoSearchPage?: string): JSX.Element => {
	return React.cloneElement(modalProps.modal, {}, modalProps.modalHeader,
		createModalBody(modalProps, refineMenu, sortByDropDown, isRecoSearchPage), modalProps.modalFooter);
};

const createModalBody = (props: ISearchResultModalViewProps, refineMenu: IRefineMenuViewProps, sortByDropDown: ISortByViewProps, isRecoSearchPage?: string): JSX.Element | null => {
	if (sortByDropDown) {
		return React.cloneElement(props.modalBody, {}, renderSort(sortByDropDown, isRecoSearchPage), renderRefiner(refineMenu));
	}
	return null;
};

const renderRefiner = (props: IRefineMenuViewProps): JSX.Element | null => {
	const { refiners, RefineMenuContainer, RefinerSectionContainer } = props;
	if (refiners){
		return (
			<Node {...RefinerSectionContainer}>
				<Node {...RefineMenuContainer}>
					{refiners.map((submenu, index) => (
						<React.Fragment key={index}>
							{submenu}
						</React.Fragment>
					))}
				</Node>
			</Node>
		);
	}
	return null;
};

const renderSort = (props: ISortByViewProps, isRecoSearchPage?: string): JSX.Element | null => {
	const { SortingContainer, sortByDropDown } = props;

	if (sortByDropDown && !isRecoSearchPage) {
		return (
			<Node {...SortingContainer}>
				{sortByDropDown}
			</Node>
		);
	}
	return null;
};

const renderCategoryHierarchy = (props: ICategoryHierarchyViewProps): JSX.Element | null => {
	const { categoryHierarchyList, categoryHierarchySeparator } = props;
	if (categoryHierarchyList && categoryHierarchyList.length > 0) {
		return (
			<nav className="breadcrumbs hierarchy">
				<a href="/" aria-label="return to home page"><svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16"><path d="M8.707 1.5a1 1 0 0 0-1.414 0L.646 8.146a.5.5 0 0 0 .708.708L8 2.207l6.646 6.647a.5.5 0 0 0 .708-.708L13 5.793V2.5a.5.5 0 0 0-.5-.5h-1a.5.5 0 0 0-.5.5v1.293L8.707 1.5Z"/><path d="m8 3.293 6 6V13.5a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 2 13.5V9.293l6-6Z"/></svg></a>
				{categoryHierarchySeparator}
				{categoryHierarchyList.map((category, index) => {
					if(category && typeof category === "object" && ('props' in category)){
						return <React.Fragment key={index}>
							<a href={`/${category && category?.props.href.substring(1).split('/').slice(1).join('/')}`} aria-label={(category && category.props.ariaLabel)}>
								{(category && category.props.text)}
							</a> {categoryHierarchyList[index + 1] && categoryHierarchySeparator}
						</React.Fragment>
					}
					return null;
				})}
			</nav>
		);
	}
	return <span></span>;	//not a good way to fix, but keeps the flex: space-between working when empty
};

const renderTitle = (props: ITitleViewProps): JSX.Element | null => {
	const { title, TitleContainer } = props;
	if (title) {
		return (<Node {...TitleContainer}><h2 id="results-title" className="text-center mt-3">{title.titleText}</h2></Node>);
	}
	return null;
};

const renderTitleCount = (props: ITitleViewProps): JSX.Element | null => {
	const { title } = props;
	if (title) {
		return (
			<div className="product-count text-muted">{title.titleCount}</div>
		);
	}
	return null;
};

export default SearchResultContainerView;
